import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import { useCallback, useRef, useSyncExternalStore, type RefObject } from "react";

import { Icon, stopClickPropagationProps, zIndexes } from "@skilitics/designer-ui-components";
import { Feedback, getTransformOriginFractions, useDraggable, type OverlayGeometry } from "@skilitics/drag-drop";

import { createTransformOriginId } from "../dnd/index.ts";
import { canvasTransformOriginDragAtom } from "./canvasAtoms.ts";

/**
 * Reads the element's transform-origin as fractions of its size, reactively.
 * geometry can't drive this — it bakes the origin into intrinsicRect and reports
 * "0 0" — so we observe the element directly: style/class for value edits (Emotion
 * rewrites the class) and resize for px-based origins. Canvas zoom/pan is
 * irrelevant since fractions are layout-space ratios.
 */
const useTransformOriginFractions = (ref: RefObject<HTMLElement | null>, width: number, height: number) => {
  const cached = useRef<{ ox: number; oy: number }>({ ox: 0.5, oy: 0.5 });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const element = ref.current;
      if (!element) return () => {};
      const mutationObserver = new MutationObserver(onChange);
      mutationObserver.observe(element, { attributes: true, attributeFilter: ["style", "class"] });
      const resizeObserver = new ResizeObserver(onChange);
      resizeObserver.observe(element, { box: "border-box" });
      return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
      };
    },
    [ref],
  );

  // Divide by the same intrinsic size the caller multiplies back by, so the
  // bases cancel and the crosshair lands on the exact computed origin.
  const getSnapshot = useCallback(() => {
    if (!ref.current) return cached.current;
    const next = getTransformOriginFractions(ref.current, width, height);
    // Return a stable reference when unchanged — required by useSyncExternalStore.
    if (next.ox !== cached.current.ox || next.oy !== cached.current.oy) cached.current = next;
    return cached.current;
  }, [ref, width, height]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

const TransformOriginContainer = styled.div`
  position: absolute;
  pointer-events: none;
`;

const TransformOriginCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  touch-action: none;
  pointer-events: auto;
  color: ${({ theme }) => theme.primary};
  z-index: ${zIndexes.composableNodeBorderSelected + 1};
  font-size: 14px;
  svg {
    overflow: visible;
    path {
      stroke: ${({ theme }) => theme.onPrimary};
      stroke-width: 2px;
      vector-effect: non-scaling-stroke;
      paint-order: stroke fill;
    }
  }
`;

type DraggableTransformOriginCircleProps = {
  nodeId: number;
  left: number;
  top: number;
  scale: number;
};

const DraggableTransformOriginCircle = ({ nodeId, left, top, scale }: DraggableTransformOriginCircleProps) => {
  const { ref } = useDraggable({
    id: createTransformOriginId(nodeId),
    plugins: [Feedback.configure({ feedback: "none" })],
  });

  return (
    <TransformOriginCircle
      ref={ref}
      style={{ left, top, transform: `translate(-50%, -50%) scale(${1 / scale})` }}
      {...stopClickPropagationProps}
    >
      <Icon icon="location-crosshairs" faPrefix="fas" />
    </TransformOriginCircle>
  );
};

type TransformOriginHandleProps = {
  nodeId: number;
  containerRef: RefObject<HTMLElement | null>;
  geometry: OverlayGeometry;
};

export const TransformOriginHandle = ({ nodeId, containerRef, geometry }: TransformOriginHandleProps) => {
  const dragState = useAtomValue(canvasTransformOriginDragAtom);
  const isDraggingThisNode = dragState?.nodeId === nodeId;

  const { width, height } = geometry.intrinsicRect;
  const scale = geometry.scaleX;

  const domFractions = useTransformOriginFractions(containerRef, width, height);
  const fractions = isDraggingThisNode && dragState ? dragState.fractions : domFractions;

  return (
    <TransformOriginContainer style={geometry.overlayStyle}>
      <DraggableTransformOriginCircle
        nodeId={nodeId}
        left={fractions.ox * width}
        top={fractions.oy * height}
        scale={scale}
      />
    </TransformOriginContainer>
  );
};
